import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { isValidUser, ROLES } from '../utils/roles';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            try {
                const response = await api.get('/users/me');
                const userData = response.data.user;

                // Validate user data structure and role
                if (!isValidUser(userData)) {
                    console.error('Invalid user data or role:', userData);
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    setUser(null);
                } else {
                    setUser(userData);
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                setUser(null);
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { accessToken, refreshToken, user } = response.data;

        // Validate user data before storing
        if (!isValidUser(user)) {
            console.error('Invalid user data received from server:', user);
            throw new Error('Invalid user data received from server');
        }

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(user);

        return response.data;
    };

    const register = async (userData) => {
        const response = await api.post('/auth/register', userData);
        const { accessToken, refreshToken, user } = response.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(user);

        return response.data;
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
        }
    };

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!user,
        hasRole: (role) => user && user.role === role,
        hasAnyRole: (roles) => user && roles.includes(user.role),
        checkAuth // Expose checkAuth for manual refresh if needed
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
